import { mdBumps, smBumps, useModal, xlBumps } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'
import { useNavigate } from 'react-router'

import { getCurrentQuote } from '../../../../../domain'
import { useCheckoutProposal } from '../../../../../hooks/checkout'
import { useEditProposal } from '../../../../../hooks/proposal'
import {
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { useUpButton } from '../../../../../infrastructure/routing'
import { YourCoverSection } from '../../../../organisms/covers'
import { ProposalDocumentsSection } from '../../../../organisms/product'
import {
  ProposalAutoRenewalSection,
  ProposalInformationSection,
} from '../../../../organisms/proposal'
import { FullPage } from '../../../../templates'
import { useProduct } from '../../../ProductProvider'
import { ProposalSummaryPricing } from '../../pricing'
import { useProposal } from '../../ProposalProvider'
import { useCheckout } from '../CheckoutProvider'
import { OutstandingBalanceModal } from './components/OutstandingBalanceModal'
import { QuoteSummarySection } from './components/QuoteSummarySection'
import { StartPaymentSection } from './components/StartPaymentSection'
import { VerificationFailedModal } from './components/VerificationFailedModal'

const tKey = TKey('pages.proposal-summary')

export const ProposalSummaryPage: FunctionComponent = () => {
  const { t } = useWriskTranslation()

  const navigate = useNavigate()
  const modal = useModal()

  const { product } = useProduct()
  const { proposal } = useProposal()
  const { previous, next } = useCheckout()

  const upButton = useUpButton(previous)

  const quote = getCurrentQuote(proposal)

  const handleEdit = useEditProposal(proposal)

  const onCheckoutBind = useCheckoutProposal(
    () => navigate(next.url),
    (checks) => {
      modal.show({
        content: <VerificationFailedModal checks={checks} />,
      })
    },
    () => modal.show({ content: <OutstandingBalanceModal /> }),
  )

  const autoRenewalSectionSubheader = t(tKey('sections.auto-renewal.subheader'), {
    defaultValue: null,
  })

  return (
    <FullPage
      pageId='proposal-summary-page'
      header={t(tKey('header'))}
      upButton={upButton}
    >
      <ProposalSummaryPricing
        product={product}
        proposal={proposal}
        quote={quote}
        mb={smBumps}
      />
      <ProposalInformationSection proposal={proposal} mb={xlBumps} />
      <ProposalAutoRenewalSection
        proposal={proposal}
        mb={xlBumps}
        subheader={autoRenewalSectionSubheader}
      />
      <QuoteSummarySection onEditDetails={handleEdit} quote={quote} />
      <YourCoverSection quote={quote} />
      <ProposalDocumentsSection quote={quote} mb={mdBumps} />
      <StartPaymentSection
        product={product}
        onConfirm={onCheckoutBind.execute}
        loading={onCheckoutBind.loading}
      />
    </FullPage>
  )
}
